/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio e rótulos literais do legado para conferência, não texto de UI */
import { CREDIT_NOTE_STATUSES, type CreditNote, type CreditNoteStatus, type CreditProduct } from './credit-note.wire';

// Tabela status × ação como dado (discovery/02 §2.5 e §3.2, D16). Alimenta o `ActionBar` da onda 2.
// Ordem = ordem do array do legado: os 4 primeiros visíveis viram botão, o resto vai para "Outras
// ações" (components/Actions/Actions.tsx:63-65, 101). `source` aponta para o item no legado.

/** Status da operação ou `''` para a operação nova (o legado lê `creditData?.status ?? ''`). */
export type StatusOrNew = CreditNoteStatus | '';

/** Permissões de ação do recurso `CreditNote` que a barra consulta (`checkActionsPerms`). */
export type CreditNoteActionPermission =
  | 'SubmitApproval'
  | 'ComplianceApproval'
  | 'CreditApproval'
  | 'InstrumentApproval'
  | 'LiquidationApproval'
  | 'SignatureValidation'
  | 'ApproveMarginReserve'
  | 'VehicleInspectionApproval'
  | 'ApproveWarranty'
  | 'ApproveWarrantyRevision'
  | 'Cancel'
  | 'AdminCancel'
  | 'ManualUpdate'
  | 'RestartWorkflow'
  | 'Clone'
  | 'AssignRecord'
  | 'UploadDocs'
  | 'SendAssigmentEmail'
  | 'ReadDataSet';

/** Condição de exibição. Todas as de uma regra valem juntas; regras de uma ação valem por "ou". */
export type ActionCondition =
  | 'existing'
  | 'new'
  | 'root'
  | 'rootOrComplianceTenant'
  | 'rootOrLiquidationTenant'
  | 'flag:approveRejectCredit'
  | 'flag:sendCreditApproval'
  | 'flag:subscriptionLink'
  | `action:${CreditNoteActionPermission}`
  | 'crud:CreditNote:Read'
  | 'crud:CreditNote:Write'
  | 'crud:CreditNote:Delete'
  | 'crud:BillingAccount:Read'
  | 'crud:BillingAccount:SendToWalletManagement'
  | 'perm:NaturalPerson:RequestDataSet'
  | 'product:officeZetra'
  | 'product:zetra'
  | 'product:fgts'
  | 'product:releaseToPaymentTrigger'
  | 'product:consignadoInss'
  | 'product:semearInss'
  | 'product:siapePublicPayroll'
  | 'product:dataprevCreditoTrabalhador'
  | 'product:privateOrPublicCategory'
  | 'record:liquidationSchedule'
  | 'record:simpleLiquidation'
  | 'record:fund'
  | 'record:installment'
  | 'record:noInstallment'
  | 'record:draftDoc'
  | 'record:noDraftDoc'
  | 'record:assignmentDoc'
  | 'record:noAssignmentDoc'
  | 'record:signedContract'
  | 'record:transitoryAccount'
  | 'record:closureOpen'
  | 'record:closureRevision'
  | 'record:transitoryOpenAccount'
  | 'record:complianceRestrictions'
  | 'person:natural';

export interface ActionRule {
  /** `any` inclui a operação nova (`''`). */
  readonly statuses: 'any' | readonly StatusOrNew[];
  readonly requires: readonly ActionCondition[];
}

/** `kind` segue a U23; `write`/`destructive` exigem prévia e confirmação (U24, D16). */
export type ActionKind = 'read' | 'navigate' | 'draft' | 'write' | 'destructive';

export interface DetailActionSpec {
  readonly id: string;
  /** Rótulo literal do legado (com os erros de digitação); o texto de UI da onda 2 sai do Lingui. */
  readonly legacyLabel: string;
  readonly kind: ActionKind;
  readonly rules: readonly ActionRule[];
  /** Aparece mas fica desabilitado sem esta condição (`disabled` do legado). */
  readonly disabledUnless?: ActionCondition;
  /** Submenu (`groupButtons`): cada item com a própria condição, além das da ação. */
  readonly group?: readonly { readonly id: string; readonly legacyLabel: string; readonly requires: readonly ActionCondition[] }[];
  readonly source: string;
}

const S = (...statuses: StatusOrNew[]): readonly StatusOrNew[] => statuses;

const CANCEL_DEFAULT = S(
  'Draft',
  'Revision',
  'ComplianceApproval',
  'InstrumentApproval',
  'CreditApproval',
  'Signatures',
  'SignaturesValidation',
  'MarginReserveApproval',
  'ManualWarranty',
  'PartnerSignaturesValidation',
  'WarrantyRevision',
);
const CANCEL_ADMIN = S(...CANCEL_DEFAULT, 'ManualLiquidation', 'WaitLiquidation', 'Liquidation', 'PaymentRevision', 'Finished', 'Error', 'Warranty');
const EXCEPT_WARRANTY = CREDIT_NOTE_STATUSES.filter((status) => status !== 'Warranty');

const one = (statuses: ActionRule['statuses'], ...requires: ActionCondition[]): readonly ActionRule[] => [{ statuses, requires }];
const FILE = 'pages/CreditNote/CreditNoteForm/CreditNoteButtons.tsx';

/** As 66 ações da barra do detalhe, na ordem de `CreditNoteButtons.tsx:480-966`. */
export const DETAIL_ACTIONS: readonly DetailActionSpec[] = [
  { id: 'submitApproval', legacyLabel: 'Enviar para aprovação', kind: 'write', rules: one(S('Disapproved', 'Error', 'Draft', 'Revision'), 'flag:sendCreditApproval', 'action:SubmitApproval'), source: `${FILE}:482 (301-309)` },
  { id: 'approveCompliance', legacyLabel: 'Aprovar compliance', kind: 'write', rules: one(S('ComplianceApproval'), 'flag:approveRejectCredit', 'rootOrComplianceTenant', 'action:ComplianceApproval'), source: `${FILE}:488 (278-288)` },
  { id: 'rejectCompliance', legacyLabel: 'Rejeitar compliance', kind: 'destructive', rules: one(S('ComplianceApproval'), 'flag:approveRejectCredit', 'rootOrComplianceTenant', 'action:ComplianceApproval'), source: `${FILE}:495 (278-288)` },
  { id: 'approveInstrument', legacyLabel: 'Aprovar instrumento', kind: 'write', rules: one(S('InstrumentApproval'), 'flag:approveRejectCredit', 'action:InstrumentApproval'), source: `${FILE}:500 (290-299)` },
  { id: 'rejectInstrument', legacyLabel: 'Rejeitar instrumento', kind: 'destructive', rules: one(S('InstrumentApproval'), 'flag:approveRejectCredit', 'action:InstrumentApproval'), source: `${FILE}:506 (290-299)` },
  { id: 'approveCredit', legacyLabel: 'Aprovar crédito', kind: 'write', rules: one(S('CreditApproval'), 'flag:approveRejectCredit', 'action:CreditApproval'), source: `${FILE}:512 (268-277)` },
  { id: 'rejectCredit', legacyLabel: 'Rejeitar crédito', kind: 'destructive', rules: one(S('CreditApproval'), 'flag:approveRejectCredit', 'action:CreditApproval'), source: `${FILE}:518 (268-277)` },
  { id: 'approveMarginReserve', legacyLabel: 'Aprovar reserva de margem', kind: 'write', rules: one(S('MarginReserveApproval'), 'action:ApproveMarginReserve'), source: `${FILE}:524 (341-349)` },
  { id: 'disapproveMarginReserve', legacyLabel: 'Rejeitar reserva de margem', kind: 'destructive', rules: one(S('MarginReserveApproval'), 'action:ApproveMarginReserve'), source: `${FILE}:530 (341-349)` },
  { id: 'approveVehicleInspection', legacyLabel: 'Aprovar vistoria veicular', kind: 'write', rules: one(S('VehicleInspectionApproval'), 'action:VehicleInspectionApproval'), source: `${FILE}:536 (351-356)` },
  { id: 'disapproveVehicleInspection', legacyLabel: 'Reprovar vistoria veicular', kind: 'destructive', rules: one(S('VehicleInspectionApproval'), 'action:VehicleInspectionApproval'), source: `${FILE}:542 (351-356)` },
  { id: 'addReserveToken', legacyLabel: 'Adicionar token consignado', kind: 'write', rules: one(EXCEPT_WARRANTY, 'existing', 'perm:NaturalPerson:RequestDataSet', 'product:officeZetra'), source: `${FILE}:548 (365-375)` },
  { id: 'checkAverbation', legacyLabel: 'Verificar averbação', kind: 'write', rules: one('any', 'existing', 'action:AdminCancel', 'product:fgts'), source: `${FILE}:554 (418-427)` },
  { id: 'approveLiquidation', legacyLabel: 'Aprovar liquidação', kind: 'write', rules: one(S('ManualLiquidation'), 'action:LiquidationApproval', 'rootOrLiquidationTenant'), source: `${FILE}:560 (220-222)` },
  { id: 'releaseToPayment', legacyLabel: 'Aprovar liberação de pagamento', kind: 'write', rules: one(S('WaitLiquidation'), 'product:releaseToPaymentTrigger', 'action:LiquidationApproval'), source: `${FILE}:568 (311-319)` },
  { id: 'rejectLiquidation', legacyLabel: 'Rejeitar liquidação', kind: 'destructive', rules: one(S('ManualLiquidation'), 'action:LiquidationApproval', 'record:simpleLiquidation', 'rootOrLiquidationTenant'), source: `${FILE}:574` },
  { id: 'approveSignature', legacyLabel: 'Aprovar assinatura', kind: 'write', rules: one(S('SignaturesValidation'), 'rootOrComplianceTenant'), source: `${FILE}:583` },
  { id: 'rejectSignature', legacyLabel: 'Rejeitar assinatura', kind: 'destructive', rules: one(S('SignaturesValidation'), 'rootOrComplianceTenant'), source: `${FILE}:590` },
  { id: 'approvePartnerSignature', legacyLabel: 'Aprovar assinatura (Parceiro)', kind: 'write', rules: one(S('PartnerSignaturesValidation'), 'action:SignatureValidation'), source: `${FILE}:597 (432-434)` },
  { id: 'rejectPartnerSignature', legacyLabel: 'Rejeitar assinatura (Parceiro)', kind: 'destructive', rules: one(S('PartnerSignaturesValidation'), 'action:SignatureValidation'), source: `${FILE}:603 (432-434)` },
  { id: 'donePaymentRevision', legacyLabel: 'Concluir revisão de pagamento', kind: 'write', rules: one(S('PaymentRevision')), source: `${FILE}:609` },
  { id: 'doneWarrantyRevision', legacyLabel: 'Concluir revisão de garantia', kind: 'write', rules: one(S('WarrantyRevision'), 'action:ApproveWarrantyRevision', 'action:ApproveWarranty'), source: `${FILE}:615` },
  { id: 'includeWarranty', legacyLabel: 'Incluir garantias', kind: 'write', rules: one(S('WarrantyRevision'), 'crud:CreditNote:Write'), source: `${FILE}:624 (438)` },
  { id: 'approveWarranty', legacyLabel: 'Aprovar garantia', kind: 'write', rules: one(S('ManualWarranty'), 'action:ApproveWarranty'), source: `${FILE}:630 (440-442)` },
  {
    id: 'disapproveWarranty',
    legacyLabel: 'Reprovar Garantia',
    kind: 'destructive',
    rules: one(S('ManualWarranty'), 'action:ApproveWarranty'),
    group: [
      { id: 'sendWarrantyToRevision', legacyLabel: 'Enviar para revisão', requires: [] },
      { id: 'cancelWarranty', legacyLabel: 'Cancelar', requires: [] },
    ],
    source: `${FILE}:636 (440-442)`,
  },
  { id: 'accountOpeningReview', legacyLabel: 'Concluir revisão de abertura de conta', kind: 'write', rules: one(S('OpenAccountRevision'), 'action:LiquidationApproval', 'record:liquidationSchedule', 'record:transitoryOpenAccount'), source: `${FILE}:662 (409-416)` },
  { id: 'completeSignature', legacyLabel: 'Concluir assinatura', kind: 'write', rules: one(S('Signatures'), 'record:signedContract'), source: `${FILE}:668 (332-339)` },
  { id: 'simulate', legacyLabel: 'Simular', kind: 'draft', rules: one(S('Draft', 'Revision', '', 'Disapproved')), source: `${FILE}:674` },
  { id: 'save', legacyLabel: 'Salvar', kind: 'write', rules: one(S('', 'Draft', 'Error', 'Disapproved', 'Revision'), 'crud:CreditNote:Write'), source: `${FILE}:680 (446-448)` },
  { id: 'reviewTransitoryAccount', legacyLabel: 'Revisar conta trasitória', kind: 'write', rules: one(S('Finished'), 'record:transitoryAccount', 'record:closureRevision', 'record:liquidationSchedule', 'action:LiquidationApproval'), source: `${FILE}:686 (464-471)` },
  { id: 'closeTransitoryAccount', legacyLabel: 'Encerrar conta trasitória', kind: 'write', rules: one(S('Finished'), 'record:transitoryAccount', 'record:closureOpen', 'record:liquidationSchedule', 'action:LiquidationApproval'), source: `${FILE}:692 (450-462)` },
  { id: 'refresh', legacyLabel: 'Atualizar', kind: 'read', rules: one('any', 'existing'), source: `${FILE}:698` },
  { id: 'checkSemearProposal', legacyLabel: 'Verificar proposta', kind: 'write', rules: one(S('Warranty'), 'product:consignadoInss', 'product:semearInss'), source: `${FILE}:709` },
  { id: 'showReserveTokens', legacyLabel: 'Visualizar tokens', kind: 'read', rules: one(S('MarginReserveApproval'), 'action:ApproveMarginReserve'), source: `${FILE}:715 (341-349)` },
  { id: 'saveDocuments', legacyLabel: 'Salvar documentos', kind: 'write', rules: one('any', 'existing', 'action:UploadDocs'), source: `${FILE}:721 (377-380)` },
  { id: 'clone', legacyLabel: 'Duplicar', kind: 'write', rules: one('any', 'existing', 'action:Clone'), source: `${FILE}:727 (474)` },
  { id: 'cloneAsNew', legacyLabel: 'Duplicar como novo', kind: 'navigate', rules: one('any', 'existing', 'action:Clone'), source: `${FILE}:733 (474)` },
  { id: 'manualUpdate', legacyLabel: 'Definir status', kind: 'write', rules: one('any', 'existing', 'action:ManualUpdate', 'root'), source: `${FILE}:739 (429-430)` },
  { id: 'manualUpdateClosureStatus', legacyLabel: 'Definir status roteiro', kind: 'write', rules: one(S('Finished'), 'record:liquidationSchedule', 'existing', 'action:ManualUpdate', 'root'), source: `${FILE}:745` },
  { id: 'updateGovernmentStatus', legacyLabel: 'Verificar averbação', kind: 'write', rules: one(S('Warranty'), 'product:zetra'), source: `${FILE}:751` },
  { id: 'linkEmployer', legacyLabel: 'Associar empregador', kind: 'write', rules: one('any', 'existing', 'action:ManualUpdate', 'root', 'product:privateOrPublicCategory'), source: `${FILE}:757` },
  { id: 'comment', legacyLabel: 'Deixar comentário', kind: 'write', rules: one('any', 'root', 'existing'), source: `${FILE}:764` },
  { id: 'assign', legacyLabel: 'Atribuir', kind: 'write', rules: one('any', 'existing', 'action:AssignRecord'), source: `${FILE}:770` },
  { id: 'delete', legacyLabel: 'Excluir', kind: 'destructive', rules: one('any', 'existing', 'crud:CreditNote:Delete'), source: `${FILE}:776` },
  {
    id: 'cancel',
    legacyLabel: 'Cancelar',
    kind: 'destructive',
    rules: [
      { statuses: 'any', requires: ['existing', 'root', 'product:zetra'] },
      { statuses: CANCEL_ADMIN, requires: ['existing', 'rootOrLiquidationTenant', 'crud:CreditNote:Read', 'action:Cancel', 'action:AdminCancel'] },
      { statuses: CANCEL_DEFAULT, requires: ['existing', 'crud:CreditNote:Read', 'action:Cancel'] },
    ],
    source: `${FILE}:782 (224-266)`,
  },
  { id: 'recoverSimulation', legacyLabel: 'Recuperar simulação', kind: 'draft', rules: one(S('Draft', '')), source: `${FILE}:788` },
  { id: 'removeRestrictions', legacyLabel: 'Remover restrições', kind: 'destructive', rules: one('any', 'root', 'record:complianceRestrictions'), source: `${FILE}:794` },
  { id: 'sendEmail', legacyLabel: 'Enviar e-mail', kind: 'write', rules: one(S('Draft', 'Signatures'), 'action:SendAssigmentEmail'), source: `${FILE}:800` },
  {
    id: 'marginQuery',
    legacyLabel: 'Consultar',
    kind: 'write',
    rules: [
      { statuses: 'any', requires: ['new', 'product:siapePublicPayroll', 'action:ReadDataSet', 'person:natural'] },
      { statuses: 'any', requires: ['product:zetra'] },
      { statuses: 'any', requires: ['product:consignadoInss'] },
    ],
    group: [
      { id: 'queryMargin', legacyLabel: 'Margem / Consultar Margem INSS', requires: [] },
      { id: 'queryConsignment', legacyLabel: 'Consignação para compra', requires: [] },
    ],
    source: `${FILE}:810 (387-396)`,
  },
  { id: 'share', legacyLabel: 'Compartilhar registro', kind: 'write', rules: one('any', 'existing', 'crud:CreditNote:Write'), source: `${FILE}:830` },
  {
    id: 'dataprevQuery',
    legacyLabel: 'Consultar Crédito Trabalhador',
    kind: 'write',
    rules: [
      { statuses: S(''), requires: ['new', 'action:ReadDataSet', 'person:natural', 'product:dataprevCreditoTrabalhador'] },
      { statuses: S('Draft', 'Revision'), requires: ['product:dataprevCreditoTrabalhador'] },
    ],
    source: `${FILE}:836 (398-407)`,
  },
  { id: 'changeCalendar', legacyLabel: 'Calendário de troco', kind: 'write', rules: one('any', 'existing', 'record:liquidationSchedule'), source: `${FILE}:841` },
  { id: 'sendSignatureLink', legacyLabel: 'Enviar link assinaturas', kind: 'write', rules: one(S('Signatures'), 'flag:subscriptionLink'), source: `${FILE}:846` },
  { id: 'generateContract', legacyLabel: 'Gerar contrato', kind: 'write', rules: one(S('Draft'), 'record:noDraftDoc'), source: `${FILE}:852` },
  { id: 'viewBilling', legacyLabel: 'Ver cobrança', kind: 'navigate', rules: one('any', 'existing', 'record:installment', 'crud:BillingAccount:Read'), source: `${FILE}:858` },
  { id: 'downloadCarne', legacyLabel: 'Download Carnê', kind: 'read', rules: one('any', 'existing', 'crud:CreditNote:Read'), disabledUnless: 'record:installment', source: `${FILE}:870` },
  { id: 'sendToWallet', legacyLabel: 'Enviar para carteira', kind: 'write', rules: one(S('Finished', 'ManualLiquidation'), 'existing', 'record:noInstallment', 'crud:BillingAccount:SendToWalletManagement'), source: `${FILE}:881` },
  { id: 'reprocess', legacyLabel: 'Reiniciar Esteira', kind: 'write', rules: one('any', 'existing', 'root', 'action:RestartWorkflow'), source: `${FILE}:891` },
  { id: 'transferReceipts', legacyLabel: 'Gerar comprovantes', kind: 'write', rules: one(S('Liquidation', 'ManualLiquidation', 'WaitLiquidation', 'Finished')), source: `${FILE}:897` },
  { id: 'viewContract', legacyLabel: 'Exibir contrato', kind: 'navigate', rules: one(S('Draft'), 'record:draftDoc'), source: `${FILE}:905` },
  { id: 'generateAssignment', legacyLabel: 'Gerar cessão', kind: 'write', rules: one(S('Draft', 'Liquidation', 'Signatures', 'SignaturesValidation', 'PaymentRevision', 'ManualLiquidation'), 'record:fund', 'record:noAssignmentDoc'), source: `${FILE}:914` },
  { id: 'viewAssignment', legacyLabel: 'Exibir cessão', kind: 'navigate', rules: one(S('Draft', 'Liquidation', 'Signatures'), 'record:assignmentDoc', 'record:fund'), source: `${FILE}:931` },
  { id: 'unlock', legacyLabel: 'Desbloquear operação', kind: 'write', rules: one(S('Draft', 'ComplianceApproval', 'Revision', 'ManualLiquidation'), 'existing', 'action:ComplianceApproval'), source: `${FILE}:943 (321-330)` },
  { id: 'fraudRecord', legacyLabel: 'Registrar fraude', kind: 'write', rules: one(S('ComplianceApproval'), 'root', 'existing', 'flag:approveRejectCredit', 'rootOrComplianceTenant', 'action:ComplianceApproval'), source: `${FILE}:949` },
  { id: 'workflows', legacyLabel: 'Esteiras', kind: 'read', rules: one('any', 'existing', 'action:RestartWorkflow'), source: `${FILE}:955` },
  { id: 'checkOperationalLimit', legacyLabel: 'Verificar Limite Operacional', kind: 'write', rules: one(S('Liquidation'), 'action:SignatureValidation'), source: `${FILE}:961` },
];

/** Fatos que a barra do detalhe consulta, já calculados (permissão, config, produto, registro). */
export interface DetailActionContext {
  readonly status: StatusOrNew;
  readonly isNew: boolean;
  readonly isRoot: boolean;
  /** `checkActionsPerms(ação, permissões, 'CreditNote')` já avaliado (shared/permissions). */
  readonly canAction: (action: CreditNoteActionPermission) => boolean;
  readonly crud: {
    readonly creditNoteRead: boolean;
    /** `Create` na nova, `Update` na existente (CreditNoteButtons.tsx:189-192). */
    readonly creditNoteWrite: boolean;
    readonly creditNoteDelete: boolean;
    readonly billingAccountRead: boolean;
    readonly billingAccountSendToWallet: boolean;
    readonly naturalPersonRequestDataSet: boolean;
  };
  readonly flags: { readonly approveRejectCredit: boolean; readonly sendCreditApproval: boolean; readonly subscriptionLink: boolean };
  readonly product: ProductFacts;
  readonly record: RecordFacts;
}

export interface ProductFacts {
  readonly complianceTenant: boolean;
  readonly liquidationTenant: boolean;
  readonly officeZetra: boolean;
  readonly zetra: boolean;
  readonly fgts: boolean;
  readonly releaseToPaymentTrigger: boolean;
  readonly consignadoInss: boolean;
  readonly semearInss: boolean;
  readonly siapePublicPayroll: boolean;
  readonly dataprevCreditoTrabalhador: boolean;
  readonly privateOrPublicCategory: boolean;
}

export interface RecordFacts {
  readonly liquidationSchedule: boolean;
  readonly fund: boolean;
  readonly installment: boolean;
  readonly draftDoc: boolean;
  readonly assignmentDoc: boolean;
  readonly signedContract: boolean;
  readonly transitoryAccount: boolean;
  readonly closureOpen: boolean;
  readonly closureRevision: boolean;
  readonly transitoryOpenAccount: boolean;
  readonly complianceRestrictions: boolean;
  readonly naturalPerson: boolean;
}

const PRIVATE_OR_PUBLIC = ['CONSIGNADO PRIVADO', 'CONSIGNADO PÚBLICO', 'CARTÃO BENEFÍCIO'];

/** `isCategoryNamePrivateOrPublic` (helpers/methods/GenericMethods.ts:375-380). */
export const isPrivateOrPublicCategory = (category: string | null | undefined) => !!category && PRIVATE_OR_PUBLIC.includes(category.toUpperCase());

/** Os fatos de produto da barra (CreditNoteButtons.tsx:210-216; services/zetra/helpers/index.ts:3-11). */
export function productFacts(product: Partial<CreditProduct> | null | undefined, userPoolId: string | null | undefined): ProductFacts {
  const office = product?.warrantyRegistrationOffice?.toLowerCase();
  const instrument = product?.instrumentType?.toLowerCase() ?? '';
  const inss = (product?.categoryName ?? '').toUpperCase() === 'CONSIGNADO INSS';
  return {
    // O legado compara com `appConfig.USER_POOL_ID`; sem produto, `undefined === undefined` daria true.
    complianceTenant: product?.complianceApprovalTenantName === userPoolId,
    liquidationTenant: product?.liquidationApprovalTenantName === userPoolId,
    officeZetra: office === 'zetra',
    zetra: instrument === 'publicpayrollloan' && office === 'zetra' && !product?.manualWarranty,
    fgts: product?.amortizationType?.toLowerCase() === 'fgts',
    releaseToPaymentTrigger: !!product?.useReleaseToPaymentTrigger,
    consignadoInss: inss,
    semearInss: office === 'semearinss',
    siapePublicPayroll: instrument === 'publicpayrollloan' && office === 'siape',
    dataprevCreditoTrabalhador: office === 'dataprevcreditotrabalhador',
    privateOrPublicCategory: isPrivateOrPublicCategory(product?.categoryName),
  };
}

/** Os fatos do registro (CreditNoteButtons.tsx:199-201, 332-339, 444-471; genericTypes.ts:148-169). */
export function recordFacts(creditNote: Partial<CreditNote> | null | undefined, complianceRestrictionCount = 0): RecordFacts {
  const uploads = creditNote?.uploads ?? [];
  const closure = creditNote?.liquidationClosureStatus;
  return {
    liquidationSchedule: creditNote?.liquidationMode === 'LiquidationSchedule',
    fund: !!creditNote?.fundId,
    installment: !!creditNote?.installmentId,
    draftDoc: uploads.some((upload) => upload.fileType === 'Draft'),
    assignmentDoc: uploads.some((upload) => upload.fileType === 'Assignment'),
    signedContract: uploads.some((upload) => upload.fileType.includes('SignedContract')),
    transitoryAccount: !!creditNote?.transitoryBankAccountId,
    closureOpen: closure === 'Pending' || closure === 'Cancelled' || !closure,
    closureRevision: closure === 'Revision',
    transitoryOpenAccount: creditNote?.transitoryBankAccountOpenAccount === true,
    complianceRestrictions: complianceRestrictionCount > 0,
    naturalPerson: creditNote?.personDiscriminator === 'NaturalPerson',
  };
}

function holdsDetail(condition: ActionCondition, ctx: DetailActionContext): boolean {
  if (condition.startsWith('action:')) return ctx.canAction(condition.slice('action:'.length) as CreditNoteActionPermission);
  switch (condition) {
    case 'existing':
      return !ctx.isNew;
    case 'new':
      return ctx.isNew;
    case 'root':
      return ctx.isRoot;
    case 'rootOrComplianceTenant':
      return ctx.isRoot || ctx.product.complianceTenant;
    case 'rootOrLiquidationTenant':
      return ctx.isRoot || ctx.product.liquidationTenant;
    case 'flag:approveRejectCredit':
      return ctx.flags.approveRejectCredit;
    case 'flag:sendCreditApproval':
      return ctx.flags.sendCreditApproval;
    case 'flag:subscriptionLink':
      return ctx.flags.subscriptionLink;
    case 'crud:CreditNote:Read':
      return ctx.crud.creditNoteRead;
    case 'crud:CreditNote:Write':
      return ctx.crud.creditNoteWrite;
    case 'crud:CreditNote:Delete':
      return ctx.crud.creditNoteDelete;
    case 'crud:BillingAccount:Read':
      return ctx.crud.billingAccountRead;
    case 'crud:BillingAccount:SendToWalletManagement':
      return ctx.crud.billingAccountSendToWallet;
    case 'perm:NaturalPerson:RequestDataSet':
      return ctx.crud.naturalPersonRequestDataSet;
    case 'product:officeZetra':
      return ctx.product.officeZetra;
    case 'product:zetra':
      return ctx.product.zetra;
    case 'product:fgts':
      return ctx.product.fgts;
    case 'product:releaseToPaymentTrigger':
      return ctx.product.releaseToPaymentTrigger;
    case 'product:consignadoInss':
      return ctx.product.consignadoInss;
    case 'product:semearInss':
      return ctx.product.semearInss;
    case 'product:siapePublicPayroll':
      return ctx.product.siapePublicPayroll;
    case 'product:dataprevCreditoTrabalhador':
      return ctx.product.dataprevCreditoTrabalhador;
    case 'product:privateOrPublicCategory':
      return ctx.product.privateOrPublicCategory;
    case 'record:liquidationSchedule':
      return ctx.record.liquidationSchedule;
    case 'record:simpleLiquidation':
      return !ctx.record.liquidationSchedule;
    case 'record:fund':
      return ctx.record.fund;
    case 'record:installment':
      return ctx.record.installment;
    case 'record:noInstallment':
      return !ctx.record.installment;
    case 'record:draftDoc':
      return ctx.record.draftDoc;
    case 'record:noDraftDoc':
      return !ctx.record.draftDoc;
    case 'record:assignmentDoc':
      return ctx.record.assignmentDoc;
    case 'record:noAssignmentDoc':
      return !ctx.record.assignmentDoc;
    case 'record:signedContract':
      return ctx.record.signedContract;
    case 'record:transitoryAccount':
      return ctx.record.transitoryAccount;
    case 'record:closureOpen':
      return ctx.record.closureOpen;
    case 'record:closureRevision':
      return ctx.record.closureRevision;
    case 'record:transitoryOpenAccount':
      return ctx.record.transitoryOpenAccount;
    case 'record:complianceRestrictions':
      return ctx.record.complianceRestrictions;
    case 'person:natural':
      return ctx.record.naturalPerson;
    default:
      return false;
  }
}

const statusMatches = (statuses: ActionRule['statuses'], status: StatusOrNew) => statuses === 'any' || statuses.includes(status);

export interface VisibleAction {
  readonly id: string;
  readonly kind: ActionKind;
  readonly disabled: boolean;
  readonly group?: readonly string[];
}

/** As ações habilitadas, na ordem do legado. `buttons` = as 4 primeiras; `more` = "Outras ações". */
export interface ActionBarModel {
  readonly actions: readonly VisibleAction[];
  readonly buttons: readonly VisibleAction[];
  readonly more: readonly VisibleAction[];
}

/** `numberOfButtons={4}` no detalhe e na lista (CreditNoteButtons.tsx:479; CreditNoteListButtons.tsx). */
export const VISIBLE_BUTTONS = 4;

function toBar(actions: readonly VisibleAction[]): ActionBarModel {
  return { actions, buttons: actions.slice(0, VISIBLE_BUTTONS), more: actions.slice(VISIBLE_BUTTONS) };
}

/** Avalia a tabela do detalhe para um contexto. */
export function visibleDetailActions(ctx: DetailActionContext): ActionBarModel {
  const visible: VisibleAction[] = [];
  for (const spec of DETAIL_ACTIONS) {
    const enabled = spec.rules.some((rule) => statusMatches(rule.statuses, ctx.status) && rule.requires.every((c) => holdsDetail(c, ctx)));
    if (!enabled) continue;
    const disabled = spec.disabledUnless ? !holdsDetail(spec.disabledUnless, ctx) : false;
    const group = spec.id === 'marginQuery' ? (ctx.product.consignadoInss ? ['queryMargin'] : ['queryMargin', 'queryConsignment']) : spec.group?.map((item) => item.id);
    visible.push({ id: spec.id, kind: spec.kind, disabled, ...(group ? { group } : {}) });
  }
  return toBar(visible);
}

// ─── lista (ações em lote) ──────────────────────────────────────────────────

/** Regra sobre as linhas selecionadas (CreditNoteListButtons.tsx:154-225). */
export type SelectionRule =
  | { readonly kind: 'none' }
  | { readonly kind: 'atLeastOne' }
  | { readonly kind: 'exactlyOne' }
  /** Todas no mesmo status e dentro da lista (`arrayStatus.every` + `checkOnlyStatusSome`). */
  | { readonly kind: 'homogeneousIn'; readonly statuses: readonly CreditNoteStatus[] }
  | { readonly kind: 'everyIn'; readonly statuses: readonly CreditNoteStatus[] }
  | { readonly kind: 'someIn'; readonly statuses: readonly CreditNoteStatus[] }
  | { readonly kind: 'everyHasFund' }
  | { readonly kind: 'everyPrivateOrPublicCategory' }
  | { readonly kind: 'everyWalletEligible' };

export type ListCondition =
  | 'root'
  | 'complianceAdmin'
  | 'flag:approveRejectCredit'
  | 'action:ManualUpdate'
  | 'action:SignatureValidation'
  | 'action:RestartWorkflow'
  | 'action:Cancel'
  | 'action:AdminCancel'
  | 'crud:CreditNote:Read'
  | 'crud:CreditNote:Create'
  | 'crud:CreditNote:Update'
  | 'crud:BillingAccount:SendToWalletManagement';

export interface ListRule {
  readonly selection: SelectionRule;
  readonly requires: readonly ListCondition[];
}

export interface ListActionSpec {
  readonly id: string;
  readonly legacyLabel: string;
  readonly kind: ActionKind;
  readonly rules: readonly ListRule[];
  /** Quantas requisições: uma por linha (§11.1), uma só com os ids, ou nenhuma. */
  readonly batch: 'perRow' | 'single' | 'none';
  readonly source: string;
}

const LIST_FILE = 'pages/CreditNote/CreditNoteList/CreditNoteButtonList/CreditNoteListButtons.tsx';
const LIST_CANCEL_DEFAULT: readonly CreditNoteStatus[] = ['Draft', 'Revision', 'ComplianceApproval', 'InstrumentApproval', 'CreditApproval', 'Signatures', 'SignaturesValidation'];
const LIST_CANCEL_ADMIN: readonly CreditNoteStatus[] = [...LIST_CANCEL_DEFAULT, 'ManualLiquidation', 'WaitLiquidation', 'Liquidation', 'PaymentRevision', 'Finished', 'Error', 'Warranty'];
const homogeneous = (...statuses: CreditNoteStatus[]): SelectionRule => ({ kind: 'homogeneousIn', statuses });
const rule = (selection: SelectionRule, ...requires: ListCondition[]): readonly ListRule[] => [{ selection, requires }];

/** As 25 ações da lista, na ordem de `CreditNoteListButtons.tsx:232-396`. */
export const LIST_ACTIONS: readonly ListActionSpec[] = [
  { id: 'refresh', legacyLabel: 'Atualizar', kind: 'read', rules: rule({ kind: 'none' }), batch: 'none', source: `${LIST_FILE}:235` },
  { id: 'newCreditNote', legacyLabel: 'Nova operação', kind: 'navigate', rules: rule({ kind: 'none' }, 'crud:CreditNote:Create'), batch: 'none', source: `${LIST_FILE}:241` },
  { id: 'importVehicleWarranty', legacyLabel: 'Importar garantia de veículos', kind: 'write', rules: rule({ kind: 'none' }, 'crud:CreditNote:Update', 'root'), batch: 'single', source: `${LIST_FILE}:246` },
  { id: 'export', legacyLabel: 'Exportar operações', kind: 'read', rules: rule({ kind: 'none' }, 'crud:CreditNote:Read'), batch: 'single', source: `${LIST_FILE}:252` },
  { id: 'linkEmployer', legacyLabel: 'Associar empregador', kind: 'write', rules: rule({ kind: 'everyPrivateOrPublicCategory' }, 'action:ManualUpdate', 'root'), batch: 'single', source: `${LIST_FILE}:265 (122-133)` },
  { id: 'approveCompliance', legacyLabel: 'Aprovar compliance', kind: 'write', rules: rule(homogeneous('ComplianceApproval')), batch: 'perRow', source: `${LIST_FILE}:272 (156-157)` },
  { id: 'rejectCompliance', legacyLabel: 'Rejeitar compliance', kind: 'destructive', rules: rule(homogeneous('ComplianceApproval')), batch: 'perRow', source: `${LIST_FILE}:278` },
  { id: 'approveCredit', legacyLabel: 'Aprovar crédito', kind: 'write', rules: rule(homogeneous('CreditApproval')), batch: 'perRow', source: `${LIST_FILE}:284 (158-159)` },
  { id: 'rejectCredit', legacyLabel: 'Rejeitar crédito', kind: 'destructive', rules: rule(homogeneous('CreditApproval')), batch: 'perRow', source: `${LIST_FILE}:290` },
  { id: 'approveSignature', legacyLabel: 'Aprovar assinatura', kind: 'write', rules: rule(homogeneous('SignaturesValidation'), 'complianceAdmin'), batch: 'perRow', source: `${LIST_FILE}:296 (227-230)` },
  { id: 'rejectSignature', legacyLabel: 'Rejeitar assinarura', kind: 'destructive', rules: rule(homogeneous('SignaturesValidation'), 'complianceAdmin'), batch: 'perRow', source: `${LIST_FILE}:302` },
  { id: 'approveInstrument', legacyLabel: 'Aprovar instrumento', kind: 'write', rules: rule(homogeneous('InstrumentApproval')), batch: 'perRow', source: `${LIST_FILE}:308` },
  { id: 'rejectInstrument', legacyLabel: 'Rejeitar instrumento', kind: 'destructive', rules: rule(homogeneous('InstrumentApproval')), batch: 'perRow', source: `${LIST_FILE}:314` },
  { id: 'approveLiquidation', legacyLabel: 'Aprovar liquidação', kind: 'write', rules: rule(homogeneous('Liquidation')), batch: 'perRow', source: `${LIST_FILE}:320 (164-165)` },
  { id: 'rejectLiquidation', legacyLabel: 'Rejeitar liquidação', kind: 'destructive', rules: rule(homogeneous('Liquidation')), batch: 'perRow', source: `${LIST_FILE}:326` },
  { id: 'approvePartnerSignature', legacyLabel: 'Aprovar assinatura (Parceiro)', kind: 'write', rules: rule(homogeneous('PartnerSignaturesValidation'), 'action:SignatureValidation'), batch: 'perRow', source: `${LIST_FILE}:331 (167-176)` },
  { id: 'rejectPartnerSignature', legacyLabel: 'Rejeitar assinatura (Parceiro)', kind: 'destructive', rules: rule(homogeneous('PartnerSignaturesValidation'), 'action:SignatureValidation'), batch: 'perRow', source: `${LIST_FILE}:337` },
  { id: 'report', legacyLabel: 'Gerar relatório', kind: 'write', rules: rule({ kind: 'atLeastOne' }), batch: 'single', source: `${LIST_FILE}:344` },
  { id: 'submitApproval', legacyLabel: 'Enviar para aprovação', kind: 'write', rules: rule({ kind: 'someIn', statuses: ['Disapproved', 'Error', 'Draft', 'Revision'] }, 'flag:approveRejectCredit'), batch: 'perRow', source: `${LIST_FILE}:350 (178-182)` },
  { id: 'sendToWallet', legacyLabel: 'Enviar para carteira', kind: 'write', rules: rule({ kind: 'everyWalletEligible' }, 'crud:BillingAccount:SendToWalletManagement'), batch: 'single', source: `${LIST_FILE}:355` },
  { id: 'reprocess', legacyLabel: 'Reiniciar Esteira', kind: 'write', rules: rule({ kind: 'atLeastOne' }, 'root', 'action:RestartWorkflow'), batch: 'single', source: `${LIST_FILE}:368` },
  { id: 'clone', legacyLabel: 'Clonar', kind: 'write', rules: rule({ kind: 'exactlyOne' }), batch: 'single', source: `${LIST_FILE}:374` },
  { id: 'startBatchAssignment', legacyLabel: ' Iniciar cessão em lote', kind: 'write', rules: rule({ kind: 'everyHasFund' }), batch: 'single', source: `${LIST_FILE}:380 (154-155)` },
  { id: 'delete', legacyLabel: 'Excluir', kind: 'destructive', rules: rule({ kind: 'everyIn', statuses: ['Revision', 'Draft', 'Canceled', 'Disapproved'] }), batch: 'perRow', source: `${LIST_FILE}:386 (183-187)` },
  {
    id: 'cancel',
    legacyLabel: 'Cancelar',
    kind: 'destructive',
    rules: [
      { selection: { kind: 'homogeneousIn', statuses: LIST_CANCEL_ADMIN }, requires: ['root', 'crud:CreditNote:Read', 'action:Cancel', 'action:AdminCancel'] },
      { selection: { kind: 'homogeneousIn', statuses: LIST_CANCEL_DEFAULT }, requires: ['crud:CreditNote:Read', 'action:Cancel'] },
    ],
    batch: 'perRow',
    source: `${LIST_FILE}:392 (189-225)`,
  },
];

/** Linha selecionada, só o que as regras leem. */
export interface SelectedRow {
  readonly status: CreditNoteStatus;
  readonly fundId?: string | null | undefined;
  readonly productCategoryDisplay?: string | null | undefined;
  readonly installmentId?: string | null | undefined;
}

export interface ListActionContext {
  readonly selected: readonly SelectedRow[];
  readonly isRoot: boolean;
  /** Usuário no grupo `Compliance_Admin` (`GET /User/{id}/Groups`, CreditNoteListButtons.tsx:106, 227-230). */
  readonly isComplianceAdmin: boolean;
  readonly canAction: (action: 'ManualUpdate' | 'SignatureValidation' | 'RestartWorkflow' | 'Cancel' | 'AdminCancel') => boolean;
  readonly crud: { readonly creditNoteRead: boolean; readonly creditNoteCreate: boolean; readonly creditNoteUpdate: boolean; readonly billingAccountSendToWallet: boolean };
  readonly flags: { readonly approveRejectCredit: boolean };
}

function holdsSelection(selection: SelectionRule, rows: readonly SelectedRow[]): boolean {
  switch (selection.kind) {
    case 'none':
      return true;
    case 'atLeastOne':
      return rows.length > 0;
    case 'exactlyOne':
      return rows.length === 1;
    case 'homogeneousIn': {
      const first = rows[0]?.status;
      return first !== undefined && selection.statuses.includes(first) && rows.every((row) => row.status === first);
    }
    case 'everyIn':
      return rows.length > 0 && rows.every((row) => selection.statuses.includes(row.status));
    case 'someIn':
      return rows.some((row) => selection.statuses.includes(row.status));
    case 'everyHasFund':
      return rows.length > 0 && rows.every((row) => !!row.fundId);
    case 'everyPrivateOrPublicCategory':
      return rows.length > 0 && rows.every((row) => isPrivateOrPublicCategory(row.productCategoryDisplay));
    case 'everyWalletEligible':
      return rows.length > 0 && rows.every((row) => (row.status === 'Finished' || row.status === 'ManualLiquidation') && !row.installmentId);
  }
}

function holdsList(condition: ListCondition, ctx: ListActionContext): boolean {
  switch (condition) {
    case 'root':
      return ctx.isRoot;
    case 'complianceAdmin':
      return ctx.isComplianceAdmin;
    case 'flag:approveRejectCredit':
      return ctx.flags.approveRejectCredit;
    case 'action:ManualUpdate':
      return ctx.canAction('ManualUpdate');
    case 'action:SignatureValidation':
      return ctx.canAction('SignatureValidation');
    case 'action:RestartWorkflow':
      return ctx.canAction('RestartWorkflow');
    case 'action:Cancel':
      return ctx.canAction('Cancel');
    case 'action:AdminCancel':
      return ctx.canAction('AdminCancel');
    case 'crud:CreditNote:Read':
      return ctx.crud.creditNoteRead;
    case 'crud:CreditNote:Create':
      return ctx.crud.creditNoteCreate;
    case 'crud:CreditNote:Update':
      return ctx.crud.creditNoteUpdate;
    case 'crud:BillingAccount:SendToWalletManagement':
      return ctx.crud.billingAccountSendToWallet;
  }
}

/** Avalia a tabela da lista para a seleção atual. */
export function visibleListActions(ctx: ListActionContext): ActionBarModel {
  const visible: VisibleAction[] = [];
  for (const spec of LIST_ACTIONS) {
    const enabled = spec.rules.some((r) => holdsSelection(r.selection, ctx.selected) && r.requires.every((c) => holdsList(c, ctx)));
    if (enabled) visible.push({ id: spec.id, kind: spec.kind, disabled: false });
  }
  return toBar(visible);
}
